import { Component, HostBinding, Input } from '@angular/core';

/**
 * Inline'owa ikonka kopiowania (Clipboard-s.svg) — jedna dla całego systemu.
 * SVG rysowany jest w DOM z fill="currentColor", więc kolor dziedziczy z CSS
 * i sam dostosowuje się do ciemnego motywu (w przeciwieństwie do <img src>).
 *
 * Stan potwierdzenia („Ikona, która się potwierdza", docs/micro-interactions-demo.html,
 * pozycja 8): oba glify leżą na sobie i przenikają się krzyżowo przez opacity i scale,
 * 160 ms. Potwierdzenie ląduje tam, gdzie patrzy użytkownik — nie na drugim końcu ekranu.
 *
 * Kiedy `confirmed` ma się zapalić, decyduje <app-copy-button>, bo tylko on wie,
 * czy kopiowanie faktycznie się udało.
 */
@Component({
  selector: 'app-copy-icon',
  template: `
    <span class="glyphs" [style.width.px]="size" [style.height.px]="size">
      <svg class="sheet" [attr.width]="size" [attr.height]="size" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M10.5 10.3085C10.5 10.4411 10.4473 10.5683 10.3536 10.6621C10.2598 10.7558 10.1326 10.8085 10 10.8085H6C5.86739 10.8085 5.74021 10.7558 5.64645 10.6621C5.55268 10.5683 5.5 10.4411 5.5 10.3085C5.5 10.1759 5.55268 10.0487 5.64645 9.95495C5.74021 9.86118 5.86739 9.8085 6 9.8085H10C10.1326 9.8085 10.2598 9.86118 10.3536 9.95495C10.4473 10.0487 10.5 10.1759 10.5 10.3085ZM10 7.8085H6C5.86739 7.8085 5.74021 7.86118 5.64645 7.95495C5.55268 8.04872 5.5 8.17589 5.5 8.3085C5.5 8.44111 5.55268 8.56829 5.64645 8.66206C5.74021 8.75582 5.86739 8.8085 6 8.8085H10C10.1326 8.8085 10.2598 8.75582 10.3536 8.66206C10.4473 8.56829 10.5 8.44111 10.5 8.3085C10.5 8.17589 10.4473 8.04872 10.3536 7.95495C10.2598 7.86118 10.1326 7.8085 10 7.8085ZM13.5 3.8085V14.3085C13.5 14.5737 13.3946 14.8281 13.2071 15.0156C13.0196 15.2031 12.7652 15.3085 12.5 15.3085H3.5C3.23478 15.3085 2.98043 15.2031 2.79289 15.0156C2.60536 14.8281 2.5 14.5737 2.5 14.3085V3.8085C2.5 3.54329 2.60536 3.28893 2.79289 3.1014C2.98043 2.91386 3.23478 2.8085 3.5 2.8085H5.76625C6.04719 2.49396 6.39139 2.24229 6.77633 2.06998C7.16127 1.89767 7.57826 1.80859 8 1.80859C8.42174 1.80859 8.83873 1.89767 9.22367 2.06998C9.6086 2.24229 9.95281 2.49396 10.2338 2.8085H12.5C12.7652 2.8085 13.0196 2.91386 13.2071 3.1014C13.3946 3.28893 13.5 3.54329 13.5 3.8085ZM6 4.8085H10C10 4.27807 9.78929 3.76936 9.41421 3.39429C9.03914 3.01922 8.53043 2.8085 8 2.8085C7.46957 2.8085 6.96086 3.01922 6.58579 3.39429C6.21071 3.76936 6 4.27807 6 4.8085ZM12.5 3.8085H10.8281C10.9419 4.12964 11 4.46782 11 4.8085V5.3085C11 5.44111 10.9473 5.56829 10.8536 5.66206C10.7598 5.75582 10.6326 5.8085 10.5 5.8085H5.5C5.36739 5.8085 5.24021 5.75582 5.14645 5.66206C5.05268 5.56829 5 5.44111 5 5.3085V4.8085C5.00001 4.46782 5.05814 4.12964 5.17188 3.8085H3.5V14.3085H12.5V3.8085Z" fill="currentColor"/>
      </svg>
      <svg class="tick" [attr.width]="size" [attr.height]="size" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M3 8.5l3.2 3.2L13 4.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>
  `,
  styles: [`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: inherit;
      opacity: 0.7;
      transition: opacity 0.2s;
    }
    :host(:hover) {
      opacity: 1;
    }
    /* Potwierdzenie ma być w pełni widoczne, niezależnie od wyciszenia spoczynkowego. */
    :host(.is-confirmed) {
      opacity: 1;
    }
    .glyphs {
      position: relative;
      display: block;
    }
    .glyphs svg {
      position: absolute;
      top: 0;
      left: 0;
      display: block;
      transition: opacity .16s ease, transform .16s ease;
    }
    .tick {
      opacity: 0;
      transform: scale(.6);
      color: #2E7D4F;
    }
    :host(.is-confirmed) .sheet {
      opacity: 0;
      transform: scale(.6);
    }
    :host(.is-confirmed) .tick {
      opacity: 1;
      transform: none;
    }
    :host-context(body.dark-mode) .tick {
      color: #55BF83;
    }
    @media (prefers-reduced-motion: reduce) {
      .glyphs svg {
        transition: none;
      }
    }
  `]
})
export class CopyIconComponent {

  @Input() size: number = 16;

  /** Stan potwierdzenia — glif kopiowania ustępuje miejsca ptaszkowi. */
  @Input() @HostBinding('class.is-confirmed') confirmed = false;
}
